'use client'

import { useState, useEffect, useCallback, useRef } from 'react';
import dynamic from 'next/dynamic';

// Lazy load Hypersphere to avoid blocking main thread with Three.js initialization
const Hypersphere = dynamic(
  () => import('@/components/Hypersphere').then(mod => ({ default: mod.Hypersphere })),
  { ssr: false }
)

import SearchDemo from '@/components/SearchDemo'
import ForAgentsMenu from '@/components/ForAgentsMenu'
import { Button, CodeCopy } from '@indxsearch/systm'
import { Search_as_you_type, Sunrise } from '@indxsearch/pixl'
import styles from './Header.module.css'

export default function Header() {
  const [isSearchFocused, setIsSearchFocused] = useState(false)
  const [heldNodeIndices, setHeldNodeIndices] = useState<number[]>([])
  const [query, setQuery] = useState('')
  const [demoOpen, setDemoOpen] = useState(false)
  const previousQueryLengthRef = useRef(0)

  // Clear held nodes when query becomes empty
  useEffect(() => {
    if (query === '') {
      setHeldNodeIndices([])
      previousQueryLengthRef.current = 0
    }
  }, [query])

  // Memoize onQueryChange to prevent infinite loops
  const handleQueryChange = useCallback((newQuery: string) => {
    const oldLength = previousQueryLengthRef.current
    const newLength = newQuery.length

    if (newLength > oldLength) {
      // Query grew - add new held node
      const randomIndex = Math.floor(Math.random() * 500)
      setHeldNodeIndices(prev => [...prev, randomIndex])
    } else if (newLength < oldLength) {
      // Query shrunk - remove last held node
      setHeldNodeIndices(prev => prev.slice(0, -1))
    }

    previousQueryLengthRef.current = newLength
    setQuery(newQuery)
  }, [])

  return (
    <div className={styles.wrapper}>
      <div className={styles.hypersphereBackground}>
        <Hypersphere
          rotationSpeed={0.02}
          firingSpeed={0.1}
          blinkDuration={0.4}
          autoFire={!isSearchFocused && query.length === 0}
          nodeCount={500}
          heldNodes={heldNodeIndices}
        />
      </div>

      <div className={styles.announce}>
        <Button
          size="default"
          variant="primary"
          className={styles.announceButton}
          iconLeft={<Sunrise />}
          href="/articles/announcing-indx-v5"
        >
          Try the new version
        </Button>
      </div>

      <div className={styles.content}>
        <div className={styles.textColumn}>
          <div className={styles.textContent}>
            <h1 className={styles.title}>
              A modern search engine with less complexity
            </h1>

            <p className={styles.tagline}>
              Add enterprise-grade search to your .NET application without servers or setup
            </p>

            <div className={styles.installRow}>
              <CodeCopy code="dotnet add package IndxSearchLib" />
              <ForAgentsMenu />
            </div>
          </div>
        </div>

        <div className={styles.searchui}>
          <div className={styles.demoToggleButton}>
            <Button
              size="default"
              variant="primary"
              className={styles.demoButton}
              iconLeft={<Search_as_you_type />}
              onClick={() => setDemoOpen(!demoOpen)}
            >
              {demoOpen ? 'Close' : 'Open live demo'}
            </Button>
          </div>
          <div className={demoOpen ? styles.demoVisible : styles.demoHidden}>
            <SearchDemo
              onSearchFocus={() => setIsSearchFocused(true)}
              onSearchBlur={() => setIsSearchFocused(false)}
              onQueryChange={handleQueryChange}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
